import { getLanguage } from '@lucid/utils';

import type { Lucid } from '../types/types';
import useSmartlingStrings from './use-smartling-strings';
import useProductUrls from './use-product-urls';

type UseNavButtonsParams = {
  shouldHideSignUpButton?: boolean;
  isLoggedIn?: boolean;
  isGlobalNav?: boolean;
  languageKey?: Lucid.I18n.LanguagesShortCode;
  product: Lucid.Site.Product;
  defaultPageUrls?: Record<string, string | undefined>;
  shouldUseDemoVideoLink?: boolean;
  shouldUseCoLogin?: boolean;
};

const useNavButtons = ({
  isLoggedIn = false,
  shouldHideSignUpButton = false,
  languageKey = 'en',
  product,
  isGlobalNav,
  defaultPageUrls = {},
  shouldUseDemoVideoLink = false,
  shouldUseCoLogin = false,
}: UseNavButtonsParams): Lucid.Directus.Generated.Menu_Link[] => {
  const { url_lang_prefix } = getLanguage(languageKey);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_nav_log_out',
      'mktg_nav_documents',
      'mktg_nav_log_in',
      'mktg_nav_try_now',
      'mktg_nav_contact_sales',
      'mktg_nav_sign_up_free',
      'mktg_nav_start_free_trial',
      'mktg_nav_watch_a_demo',
    ],
    shortCode: languageKey,
  });

  const { activateUrl, pricingUrl, productUrl } = useProductUrls(url_lang_prefix, product);

  const buttons: Record<string, Lucid.Directus.Generated.Menu_Link> = {
    'log-out': {
      id: 'log-out',
      final_url: `${productUrl}/users/logout`,
      display_as_button: true,
      beacon_id: 'log-out',
      default_link_text: withSmartling('mktg_nav_log_out'),
    },
    'log-in': {
      id: 'log-in',
      final_url: activateUrl,
      display_as_button: true,
      default_link_text: withSmartling('mktg_nav_log_in'),
      beacon_id: 'log-in',
    },
    documents: {
      id: 'documents',
      final_url: `${productUrl}/documents`,
      display_as_button: true,
      default_link_text: withSmartling('mktg_nav_documents'),
      beacon_id: 'documents',
    },
    'contact-sales': {
      id: 'contact-sales',
      final_url: defaultPageUrls.contact_sales_url ?? 'https://lucid.co/contact/contact-sales',
      display_as_button: true,
      default_link_text: withSmartling('mktg_nav_contact_sales'),
      beacon_id: 'contact-sales',
    },
    'sign-up-free': {
      id: 'sign-up-free',
      final_url: pricingUrl,
      display_as_button: true,
      default_link_text: withSmartling('mktg_nav_sign_up_free'),
      beacon_id: 'sign-up-free',
    },
    'start-free-trial': {
      id: 'start-free-trial',
      final_url: pricingUrl,
      display_as_button: true,
      default_link_text: withSmartling('mktg_nav_start_free_trial'),
      beacon_id: 'sign-up-free',
    },
    'try-now': {
      id: 'try-now',
      final_url: defaultPageUrls.try_now_url || 'https://lucid.co/try-now',
      display_as_button: true,
      default_link_text: withSmartling('mktg_nav_try_now'),
      beacon_id: 'try-now',
    },
    'consensus-demo': {
      id: 'consensus-demo',
      final_url: defaultPageUrls.video_demo_url || 'https://lucid.co/watch-a-demo',
      display_as_button: true,
      default_link_text: withSmartling('mktg_nav_watch_a_demo'),
    },
    assignments: {
      id: 'assignments',
      final_url: '/assignments',
      display_as_button: true,
      default_link_text: 'Education templates',
      beacon_id: 'assignments',
    },
  };

  const asLink = (button: Lucid.Directus.Generated.Menu_Link): Lucid.Directus.Generated.Menu_Link => ({
    ...button,
    display_as_button: false,
  });

  const buttonsByProduct: Partial<
    Record<
      Lucid.Site.Product,
      Record<'globalNav' | 'siteNav', Record<'loggedIn' | 'loggedOut', Lucid.Directus.Generated.Menu_Link[]>>
    >
  > = {
    chartblog: {
      globalNav: {
        loggedIn: [asLink(buttons['log-out'])],
        loggedOut: [asLink(buttons['log-in'])],
      },
      siteNav: {
        loggedIn: [asLink(buttons['try-now']), buttons['contact-sales']],
        loggedOut: [asLink(buttons['try-now']), buttons['contact-sales']],
      },
    },
    chart: {
      globalNav: {
        loggedIn: [asLink(buttons['log-out'])],
        loggedOut: [asLink(buttons['log-in'])],
      },
      siteNav: {
        loggedIn: [
          shouldUseDemoVideoLink ? asLink(buttons['consensus-demo']) : asLink(buttons['contact-sales']),
          buttons['documents'],
        ],
        loggedOut: [
          shouldUseDemoVideoLink ? asLink(buttons['consensus-demo']) : asLink(buttons['contact-sales']),
          ...(shouldHideSignUpButton ? [] : [buttons['sign-up-free']]),
        ],
      },
    },
    co: {
      globalNav: {
        loggedIn: [asLink(buttons['log-out'])],
        loggedOut: [asLink(buttons['log-in'])],
      },
      siteNav: {
        loggedIn: shouldUseCoLogin
          ? [asLink(buttons['log-in']), buttons['try-now'], buttons['contact-sales']]
          : [asLink(buttons['try-now']), buttons['contact-sales']],
        loggedOut: shouldUseCoLogin
          ? [asLink(buttons['log-in']), buttons['try-now'], buttons['contact-sales']]
          : [asLink(buttons['try-now']), buttons['contact-sales']],
      },
    },
    edu: {
      globalNav: {
        loggedIn: [asLink(buttons['log-out'])],
        loggedOut: [asLink(buttons['log-in'])],
      },
      siteNav: {
        loggedIn: [asLink(buttons['contact-sales']), buttons['assignments']],
        loggedOut: [asLink(buttons['contact-sales']), buttons['assignments']],
      },
    },
    scale: {
      globalNav: {
        loggedIn: [asLink(buttons['log-out'])],
        loggedOut: [asLink(buttons['log-in'])],
      },
      siteNav: {
        loggedIn: [buttons['documents']],
        loggedOut: [buttons['contact-sales']],
      },
    },
    spark: {
      globalNav: {
        loggedIn: [asLink(buttons['log-out'])],
        loggedOut: [asLink(buttons['log-in'])],
      },
      siteNav: {
        loggedIn: [
          shouldUseDemoVideoLink ? asLink(buttons['consensus-demo']) : asLink(buttons['contact-sales']),
          buttons['documents'],
        ],
        loggedOut: [
          shouldUseDemoVideoLink ? asLink(buttons['consensus-demo']) : asLink(buttons['contact-sales']),
          ...(shouldHideSignUpButton ? [] : [buttons['sign-up-free']]),
        ],
      },
    },
  };

  return (
    buttonsByProduct[product]?.[isGlobalNav ? 'globalNav' : 'siteNav']?.[isLoggedIn ? 'loggedIn' : 'loggedOut'] ?? []
  );
};

export default useNavButtons;
